/**
 * Money going out that is not stock.
 *
 * Rent, salaries, electricity, fuel. Until this existed the books could see
 * revenue and food cost and nothing else, so the profit figure was a gross
 * margin being read as a profit.
 *
 * WHY IT ASKS WHAT THE MONEY WAS FOR
 * ----------------------------------
 * "Miscellaneous, Rs. 40,000" is the entry nobody can account for a month
 * later, and it is also how money leaves a business without anyone noticing.
 * The description is required, and the category decides which account it lands
 * in — so the P&L reads properly without anyone sorting it out afterwards.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR, today } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field } from '../../components/ui/Field.jsx';
import { useAuth } from '../../stores/auth.js';
import { useTradingDay } from '../pos/useBusinessDay.js';

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'petty_cash', label: 'Petty cash' },
];

/**
 * A plain YYYY-MM-DD read as that calendar day, whatever the browser's zone.
 *
 * The selected day is a bare date, not a moment. Rendering it through a Date
 * parsed as UTC shifts it back a day for any browser west of Greenwich — so the
 * heading read "19 Sept" for the 20th. Building a local Date from the parts
 * keeps the day the day.
 */
function labelForDay(ymd) {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y) return ymd || '—';
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
}

export default function ExpensesPage() {
  const client = useQueryClient();
  const [recording, setRecording] = useState(false);
  // Day by day, not a whole month at once: the accountant sits down with a
  // day's receipts, picks that day, and enters them against it.
  const [day, setDay] = useState(() => today());

  const expenses = useQuery({
    queryKey: ['expenses', day],
    queryFn: () => api.get(withQuery('/accounts/expenses', { from: day, to: day, limit: 500 })),
  });

  const byCategory = useQuery({
    queryKey: ['expenses', 'by-category', day],
    queryFn: () => api.get(withQuery('/accounts/expenses/by-category', { from: day, to: day })),
  });

  const rows = expenses.data?.expenses ?? [];
  const total = expenses.data?.total ?? 0;
  const stepDay = (delta) => {
    const d = new Date(`${day}T00:00:00`);
    d.setDate(d.getDate() + delta);
    setDay(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Expenses</h1>
          <p className="mt-0.5 text-sm text-ink-2">
            Everything the business spends that is not stock, a day at a time.
          </p>
        </div>

        <div className="flex items-end gap-2">
          <label>
            <span className="mb-1.5 block text-sm font-medium text-ink-2">Day</span>
            <div className="flex items-stretch gap-1">
              <button
                type="button"
                aria-label="Previous day"
                onClick={() => stepDay(-1)}
                className="h-12 w-10 rounded-lg border border-rule bg-surface-3 text-ink-2 hover:bg-rule"
              >
                ‹
              </button>
              <input
                type="date"
                aria-label="Day"
                value={day}
                max={today()}
                onChange={(e) => e.target.value && setDay(e.target.value)}
                className="h-12 rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
              <button
                type="button"
                aria-label="Next day"
                onClick={() => stepDay(1)}
                disabled={day >= today()}
                className="h-12 w-10 rounded-lg border border-rule bg-surface-3 text-ink-2 hover:bg-rule disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </label>
          <Button size="lg" onClick={() => setRecording(true)}>
            Record a cost
          </Button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-rule bg-surface p-3.5">
          <p className="text-xs text-ink-3">Spent on {labelForDay(day)}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">{formatPKR(total)}</p>
        </div>

        {(byCategory.data?.categories ?? []).slice(0, 3).map((row) => (
          <div key={row.category} className="rounded-lg border border-rule bg-surface px-3 py-2.5">
            <p className="truncate text-xs text-ink-3">{row.category}</p>
            <p className="mt-0.5 font-medium tabular-nums text-ink">{formatPKR(row.total)}</p>
          </div>
        ))}
      </div>

      <DataTable
        rows={rows}
        loading={expenses.isLoading}
        error={expenses.isError}
        searchable
        searchKeys={['description', 'vendor_name', 'category_name', 'expense_number']}
        empty="Nothing recorded for this month."
        columns={[
          { key: 'payment_date', label: 'Date', render: (row) => labelForDay(row.payment_date) },
          { key: 'category_name', label: 'Category', tone: () => 'text-ink' },
          { key: 'description', label: 'What for' },
          { key: 'vendor_name', label: 'Paid to' },
          {
            key: 'payment_method',
            label: 'How',
            render: (row) =>
              METHODS.find((m) => m.value === row.payment_method)?.label ?? row.payment_method,
          },
          {
            key: 'account_code',
            label: 'Account',
            // The account it landed in, so a mis-filed cost can be spotted
            // without opening the ledger.
            tone: () => 'text-ink-3',
          },
          {
            key: 'total_amount',
            label: 'Amount',
            align: 'right',
            render: (row) => formatPKR(row.total_amount),
            tone: () => 'text-ink',
          },
        ]}
      />

      {recording && (
        <RecordExpense
          defaultDate={day}
          onClose={() => setRecording(false)}
          onRecorded={() => {
            setRecording(false);
            client.invalidateQueries({ queryKey: ['expenses'] });
            // The profit figure on the dashboard has just changed.
            client.invalidateQueries({ queryKey: ['reports'] });
          }}
        />
      )}
    </div>
  );
}

const CUSTOM = '__custom__';

function RecordExpense({ onClose, onRecorded, defaultDate }) {
  const [categoryId, setCategoryId] = useState('');
  const [customKind, setCustomKind] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  // The trading day the shop has open: where an expense lands unless an
  // owner, manager or accountant chooses another day. Everyone else records
  // against it, and it moves only when the day is closed.
  const tradingDay = useTradingDay();
  const mayBackdate = useAuth((s) => s.hasRole('owner', 'super_admin', 'branch_manager', 'accountant'));
  const [picked, setPaymentDate] = useState(null);
  const paymentDate = picked ?? tradingDay ?? defaultDate ?? today();

  const categories = useQuery({
    queryKey: ['expenses', 'categories'],
    queryFn: () => api.get('/accounts/expense-categories'),
    staleTime: 60 * 60_000,
  });

  const record = useMutation({
    mutationFn: (body) => api.post('/accounts/expenses', body),
    onSuccess: onRecorded,
  });

  const value = Number(amount) || 0;
  const isCustom = categoryId === CUSTOM;
  const chosen = (categories.data?.categories ?? []).find((c) => c.id === categoryId);
  const valid =
    value > 0 && description.trim() && (isCustom ? customKind.trim() : Boolean(categoryId));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Record a cost"
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          record.mutate({
            ...(isCustom
              ? { categoryName: customKind.trim() }
              : { categoryId }),
            amount: value,
            description: description.trim(),
            vendorName: vendorName.trim() || undefined,
            paymentMethod,
            paymentDate,
          });
        }}
        className="w-full max-w-md rounded-2xl bg-surface p-5"
      >
        <h2 className="font-semibold text-lg text-ink">Record a cost</h2>

        <label className="mt-4 block">
          <span className="mb-1.5 block text-sm font-medium text-ink-2">What kind</span>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            required
            className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          >
            <option value="">Choose one</option>
            {(categories.data?.categories ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
            {/* For a cost that fits none of the above — the accountant types
                their own kind. */}
            <option value={CUSTOM}>Something else…</option>
          </select>

          {isCustom ? (
            <>
              <input
                type="text"
                aria-label="Custom kind"
                value={customKind}
                onChange={(e) => setCustomKind(e.target.value)}
                placeholder="Type the kind, e.g. Generator repair"
                className="mt-2 h-12 w-full rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
              <span className="mt-1.5 block text-xs text-ink-3">
                A new kind — posts to Miscellaneous Expenses, and appears in the list from now on.
              </span>
            </>
          ) : (
            chosen && (
              // Shown so a mis-filed cost is obvious before it is saved rather
              // than a month later in the P&L.
              <span className="mt-1.5 block text-xs text-ink-3">
                Posts to {chosen.account_code} {chosen.account_name}
              </span>
            )
          )}
        </label>

        <Field
          className="mt-4"
          label="How much"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0"
        />

        <Field
          className="mt-4"
          label="What for"
          required
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="August rent"
          hint="A month from now this is all anyone will have to go on."
        />

        <Field
          className="mt-4"
          label="Paid to (optional)"
          value={vendorName}
          onChange={(e) => setVendorName(e.target.value)}
          placeholder="Landlord, LESCO, …"
        />

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label>
            <span className="mb-1.5 block text-sm font-medium text-ink-2">How paid</span>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            >
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          {mayBackdate ? (
            <Field label="When" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          ) : (
            <div>
              <span className="mb-1.5 block text-sm font-medium text-ink-2">When</span>
              <div className="flex h-12 items-center rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink" aria-label="Trading day">
                {labelForDay(paymentDate)}
              </div>
              <p className="mt-1 text-xs text-ink-3">The trading day that is open. It moves when the day is closed.</p>
            </div>
          )}
        </div>

        {record.error && (
          <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
            {record.error.message}
          </p>
        )}

        <div className="mt-5 flex gap-2">
          <Button type="button" variant="secondary" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="lg"
            className="flex-1"
            busy={record.isPending}
            disabled={!valid}
          >
            Record {value > 0 ? formatPKR(value) : ''}
          </Button>
        </div>
      </form>
    </div>
  );
}
